import { message } from 'antd';
import { addWordToList } from '@/libs/api';
import type { Translation, WordDataType } from '@/libs/types';
import { getErrorMessage } from '@/utils/errorHandler';

// 解析单词内容
export const parseWordContent = (content: string) => {
  const parts = content.split('<br>');
  const mainWord = parts[0];
  const meanings: string[] = [];
  const examples: string[] = [];

  // 解析主要单词部分
  let word = '',
    phonetic = '';
  if (mainWord.includes('【')) {
    const [w, r] = mainWord.split('【');
    word = w.trim();
    phonetic = r.replace('】', '').trim();
  } else {
    word = mainWord.trim();
  }

  // 解析释义和例句
  parts.slice(1).forEach(part => {
    if (!part.trim()) return;

    if (part.startsWith('（')) {
      // 这是释义，提取序号和内容
      const meaning = part.replace(/[（）]/g, '').trim();
      const match = meaning.match(/^(\d+)(.+)$/);
      if (match) {
        meanings.push(`${match[1]}${match[2]}`); // 保留序号和内容的格式
      } else {
        meanings.push(meaning);
      }
    } else {
      // 这是例句，保持原有格式
      examples.push(part.trim());
    }
  });

  return {
    word,
    phonetic,
    meanings,
    examples,
  };
};

interface AddWordData {
  content: string;
  source: string;
  translations: Translation[];
  phonetic?: string;
  example?: string;
  dictId?: number;
  aiTranslation?: string;
  aiTranslationId?: number;
}

// 解析词典内容
export const parseDictionaryResult = (result: {
  id?: number;
  w?: string;
  m: string;
  notFound?: boolean;
}) => {
  const { id, w, m } = result;
  const translations: Translation[] = [];
  let word = w || ''; // 使用原始查询词
  let phonetic = '';
  let example = '';

  // 移除标记
  const content = m.replace(/`1`|`2`/g, '');
  const lines = content.split('<br>');

  // 处理第一行（单词和读音）
  if (lines[0]) {
    if (lines[0].includes('【')) {
      const [mainWord, reading] = lines[0].split('【');
      if (!word) {
        word = mainWord.trim();
      }
      phonetic = reading.replace('】', '').trim();
    } else if (!word) {
      word = lines[0].trim();
    }
  }

  let currentPos = '';
  let currentMeanings: string[] = [];

  // 处理剩余行
  lines.slice(1).forEach(line => {
    if (!line.trim()) return;

    if (line.startsWith('（')) {
      // 如果有之前的词性和释义，保存它们
      if (currentMeanings.length > 0) {
        translations.push({
          pos: currentPos,
          meanings: [...currentMeanings],
        });
        currentMeanings = [];
      }
      // 提取序号和释义，但不使用序号作为词性
      const posMatch = line.match(/（(\d+)）(.+)$/);
      if (posMatch) {
        currentPos = ''; // 不使用序号作为词性
        currentMeanings.push(posMatch[2].trim());
      } else {
        // 如果没有序号，可能是词性标记
        const content = line.replace(/[（）]/g, '').trim();
        if (
          content.match(/^(n\.|v\.|adj\.|adv\.|pron\.|conj\.|int\.|aux\.|part\.|prefix|suffix)$/)
        ) {
          currentPos = content;
        } else {
          currentMeanings.push(content);
        }
      }
    } else if (line.includes('／')) {
      // 这是一个例句
      const parts = line.split('／');
      if (parts.length > 1) {
        example = example || `${parts[0].trim()} / ${parts[1].trim()}`;
      } else {
        example = example || parts[0].trim();
      }
    } else if (!line.includes('【')) {
      // 这是一个释义
      currentMeanings.push(line.trim());
    }
  });

  // 保存最后一组词性和释义
  if (currentMeanings.length > 0) {
    translations.push({
      pos: currentPos,
      meanings: [...currentMeanings],
    });
  }

  return {
    word, // 保持原始查询词
    phonetic,
    translations,
    example,
    dictId: id,
  };
};

// 将前端的 Translation[] 转换为 API 期望的格式
const convertToApiFormat = (translations: Translation[]): unknown[][] => {
  return translations.map(trans => {
    if (trans.pos) {
      return [trans.pos, ...trans.meanings];
    }
    return trans.meanings;
  });
};

// 添加单词到单词本
export const handleAddToVocabularyList = async (
  listId: number,
  wordData:
    | AddWordData
    | {
        searchWord: string;
        content: string;
        dictionaryResult: { id?: number; w?: string; m: string; notFound?: boolean };
      },
): Promise<boolean> => {
  try {
    let apiData: WordDataType;

    if ('dictionaryResult' in wordData) {
      // 处理词典结果，直接使用解析后的数据
      const parsedData = parseDictionaryResult(wordData.dictionaryResult);
      apiData = {
        word: wordData.searchWord,
        phonetic: parsedData.phonetic,
        translations: convertToApiFormat(parsedData.translations),
        example: parsedData.example,
        dictId: parsedData.dictId,
      };
    } else {
      // 处理自定义添加，需要用户编辑释义
      apiData = {
        word: wordData.content,
        phonetic: wordData.phonetic,
        translations: convertToApiFormat(wordData.translations),
        example: wordData.example,
        dictId: wordData.dictId,
        aiTranslation: wordData.aiTranslation,
        aiTranslationId: wordData.aiTranslationId,
      };
    }

    await addWordToList(listId, {
      word: apiData.word,
      wordData: apiData,
    });

    return true;
  } catch (error: unknown) {
    const errorMessage = getErrorMessage(error, '添加失败，请重试');
    message.error(errorMessage);
    return false;
  }
};
